/**
 * The provider stack and the always-on chrome, mounted INSIDE the router rather than around it.
 *
 * That placement is load-bearing and is the one real difference between Next.js and React Router
 * here. Next's `useRouter()` read an app-global router, so a provider could call it from anywhere in
 * the tree. React Router's `useNavigate()` reads React context, so any component that calls it must
 * be a descendant of the RouterProvider. SessionProvider navigates on sign-out, so wrapping the
 * router in it (the obvious translation of the old layout.tsx) throws "useNavigate() may be used
 * only in the context of a <Router> component" before the app can mount.
 *
 * The sandbox banner sits above everything, on every page, always. It is the first thing a
 * participant sees and it cannot be permanently dismissed: a disclosure that can be turned off is not
 * a disclosure. It stays a component rather than moving into index.html because it is stateful.
 *
 * One session, fetched once, shared by every panel. The tier lives here and NOWHERE else, because
 * UF_00 calls tier gating "the single most important cross panel rule" and a rule with two copies is
 * a rule with two answers. MotionProvider wraps it so every animated component inherits
 * `reducedMotion="user"`: one place decides that a participant who asked for less motion gets it.
 */

import { Outlet } from 'react-router-dom';

import { SandboxBanner } from '@/components/chrome';
import { ConfirmProvider } from '@/components/confirm';
import { MotionProvider } from '@/components/motion';
import { SessionProvider } from '@/lib/session-context';

export function Root() {
  return (
    <>
      <SandboxBanner />
      <MotionProvider>
        <SessionProvider>
          {/* Destructive confirmations are the app's own dialog, not the browser's: a native
              window.confirm cannot be themed, cannot be reached consistently by a screen reader,
              and on a page about money it looks like something the page did not mean to say. */}
          <ConfirmProvider>
            <Outlet />
          </ConfirmProvider>
        </SessionProvider>
      </MotionProvider>
    </>
  );
}
